import { useTranslation } from 'react-i18next';
import { Dispatch, SetStateAction } from 'react';
import { AbortedErrorName, ErrorWithMessageAndResponseStatus, isNetworkError } from '@/constants/common';
import { useAuth0 } from '@auth0/auth0-react';
import { apiConfidentialCountries, apiGetAvatarLink, apiMe, apiPresignedAvatarUrl } from '@/utils/apiUrls';
import { App } from 'antd';
import { useAuth } from '@/context/AuthContext';
import { MeUserDataResponse } from '@/types/currentUser';
import { Item } from '@/types/common';

const PII_KEYS = [
  'email',
  'emails',
  'first_name',
  'last_name',
  'name',
  'full_name',
  'full_names',
  'contact_email',
  'phone',
];
const emailLikeRegex = /^[^\s@]+@[^\s@]+$/;

const maskString = (value: string): string =>
  value.length <= 1 ? '*' : `${value[0]}${'*'.repeat(value.length - 1)}`;

const maskEmail = (value: string): string => {
  const atIndex = value.indexOf('@');
  if (atIndex <= 0) return maskString(value);
  return `${maskString(value.slice(0, atIndex))}${value.slice(atIndex)}`;
};

const maskPii = (data: unknown, keyHint?: string): unknown => {
  if (typeof data === 'string') {
    if (emailLikeRegex.test(data)) return maskEmail(data);
    if (keyHint && PII_KEYS.includes(keyHint.toLowerCase())) return maskString(data);
    return data;
  }
  if (Array.isArray(data)) return data.map((item) => maskPii(item, keyHint));
  if (data && typeof data === 'object') {
    return Object.fromEntries(Object.entries(data).map(([key, value]) => [key, maskPii(value, key)]));
  }
  return data;
};

const serializeRequestBody = (body: BodyInit | null | undefined): unknown => {
  if (!body) return undefined;
  if (typeof body === 'string') {
    try {
      return JSON.parse(body);
    } catch {
      return body;
    }
  }
  if (body instanceof FormData) {
    const result: Record<string, unknown> = {};
    body.forEach((value, key) => {
      result[key] = value instanceof File ? `[File: ${value.name}]` : value;
    });
    return result;
  }
  return undefined;
};

export const useCommonFetchers = () => {
  const { t } = useTranslation();
  const { isAuthenticated, getAccessTokenSilently } = useAuth0();
  const { message } = App.useApp();
  const { setExpiredSessionError } = useAuth();

  const getAuthToken = async (setIsLoading?: Dispatch<SetStateAction<boolean>>): Promise<string | null> => {
    if (!isAuthenticated) return null;
    try {
      return await getAccessTokenSilently({
        authorizationParams: { audience: process.env.AUTH0_AUDIENCE, scope: 'read:current_user' },
      });
    } catch (error) {
      if (setIsLoading) setIsLoading(false);
      setExpiredSessionError(true);
      throw error;
    }
  };

  const fetcher = async (
    url: string,
    setIsLoading?: Dispatch<SetStateAction<boolean>>,
    options: RequestInit = {},
    errorHandler?: (err: ErrorWithMessageAndResponseStatus) => void,
    statusForErrorHandler?: number,
    tokenCanBeSet = true,
  ): Promise<any> => {
    // Prepare the headers
    const headers = new Headers(options.headers || {});
    headers.set('Accept', `application/json`);

    let shouldShowCustomError = false;

    if (tokenCanBeSet) {
      const token = await getAuthToken(setIsLoading);
      if (token) headers.set('Authorization', `Bearer ${token}`);
    }

    const fetchOptions = {
      ...options,
      headers: headers,
    };

    try {
      const response = await fetch(url, fetchOptions as RequestInit);

      if (!response.ok) {
        if (response.status >= 500 && response.status < 600) {
          throw new ErrorWithMessageAndResponseStatus(t('common.messages.somethingWentWrong'), response.status);
        } else {
          const errorText = await response.text();
          let errorMessage;

          try {
            errorMessage = JSON.parse(errorText).error || JSON.parse(errorText).message;
          } catch {
            errorMessage = errorText;
          }

          if (errorHandler && statusForErrorHandler === response.status) {
            shouldShowCustomError = true;
          }

          throw new ErrorWithMessageAndResponseStatus(errorMessage, response.status);
        }
      }

      if (response.status === 204) {
        return null;
      }

      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        return await response.json();
      } else {
        return response;
      }
    } catch (error: any) {
      if (error.name === AbortedErrorName) return;

      if (setIsLoading) setIsLoading(false);

      if (
        error instanceof ErrorWithMessageAndResponseStatus &&
        error.responseStatus >= 400 &&
        error.responseStatus < 500
      ) {
        const serializedBody = serializeRequestBody(options.body);
        error.requestPayload = {
          url,
          method: (fetchOptions.method ?? 'GET').toString(),
          body: maskPii(serializedBody),
        };
      }

      if (isNetworkError(error)) {
        error.wasOffline = typeof navigator !== 'undefined' && !navigator.onLine;
        message.error(t('common.messages.somethingWentWrong'));
      } else if (errorHandler && shouldShowCustomError) {
        errorHandler(error);
      } else if (error instanceof ErrorWithMessageAndResponseStatus) {
        message.error(error.message);
      } else {
        message.error(error.message);
      }

      throw error;
    }
  };

  const getPresignedAvatarUrl = async (setIsLoading: Dispatch<SetStateAction<boolean>>) => {
    return fetcher(apiPresignedAvatarUrl(), setIsLoading, { method: 'POST' });
  };

  const getAvatar = async (setIsLoading: Dispatch<SetStateAction<boolean>>) => {
    return fetcher(apiGetAvatarLink(), setIsLoading);
  };

  const getMe = async (setIsLoading: Dispatch<SetStateAction<boolean>>): Promise<MeUserDataResponse> => {
    return fetcher(apiMe(), setIsLoading);
  };

  const uploadFileToS3 = async (
    url: string,
    file: Blob,
    setIsLoading?: Dispatch<SetStateAction<boolean>>,
  ): Promise<any> => {
    return fetcher(
      url,
      setIsLoading,
      {
        method: 'PUT',
        headers: {
          'Content-Type': file.type,
        },
        body: file,
      },
      undefined,
      undefined,
      false,
    );
  };

  const getConfidentialCountries = async (setIsLoading: Dispatch<SetStateAction<boolean>>): Promise<string[]> => {
    const { data }: { data: Item[] } = await fetcher(apiConfidentialCountries(), setIsLoading);

    return data.map((option) => option.name);
  };

  return {
    fetcher,
    getAuthToken,
    getMe,
    getAvatar,
    getPresignedAvatarUrl,
    uploadFileToS3,
    getConfidentialCountries,
  };
};
